import { useState, useRef, useEffect, useMemo, useCallback, Fragment, type JSX } from 'react'
import {
  useInstagramChatMessages,
  type InstagramChatMessage,
  type PendingInstagramMessage,
} from '../../hooks/useInstagramChats.js'
import { InstagramIcon } from '../conversations/BrandIcons.js'
import { TicketGroupBlock } from '../chat-shared/TicketGroupBlock.js'
import { groupMessagesByTicket } from '../chat-shared/ticketGrouping.js'
import { dayBoundaries } from '../chat-shared/dayGrouping.js'
import { placeHandoffEvents } from '../chat-shared/timeline.js'
import { HandoffDivider } from '../chat-shared/HandoffDivider.js'
import { useStickyScroll } from '../chat-shared/useStickyScroll.js'
import { useChannelTyping } from '../chat-shared/useChannelTyping.js'
import { useTypingSignal } from '../chat-shared/useTypingSignal.js'
import { TypingIndicator } from '../chat-shared/TypingIndicator.js'
import { Send, MessageCircle, Ticket, X, Loader2, Check, AlertCircle, ChevronDown, ArrowRightLeft, Bot, UserPlus } from 'lucide-react'
import { formatInTimezone } from '../../utils/timezone.js'
import { ToolButton } from '../../components/conversations/detail/primitives.js'
import { api } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'

interface Props {
  chatId: string | null
  isAssignedToMe?: boolean
  isAiAssigned?: boolean
  aiAgentName?: string | null
  onOpenTransfer?: () => void
  onOpenDetails?: () => void
}

function MessageBubble({ msg }: { msg: InstagramChatMessage }): JSX.Element {
  const isOutbound = msg.direction === 'outbound'
  // Outbound rows stored by the worker start as 'sending' and end 'sent' or 'failed'.
  const failed = isOutbound && msg.status === 'failed'
  const sending = isOutbound && msg.status === 'sending'
  return (
    <div className={`flex flex-col ${isOutbound ? 'items-end' : 'items-start'}`}>
      {isOutbound && msg.agent_name && (
        <span className="mb-0.5 pr-1 text-[10px] font-medium text-gray-500">{msg.agent_name}</span>
      )}
      <div
        className={`relative max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
          failed ? 'border border-red-200 bg-red-50 text-gray-700' : isOutbound ? 'bg-[#E4405F] text-white' : 'border border-gray-100 bg-white text-gray-800 shadow-sm'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{msg.content_text || '[media]'}</p>
        <div className="mt-1 flex items-center justify-end gap-1">
          <span className={`text-[10px] ${isOutbound ? 'text-pink-100' : 'text-gray-400'}`}>
            {formatInTimezone(msg.sent_at, { hour: '2-digit', minute: '2-digit' })}
          </span>
          {failed && <span className="text-[10px] text-red-500">Not sent</span>}
          {sending && <span className="text-[10px] text-pink-100">Sending…</span>}
          {isOutbound && !failed && !sending && <Check className="h-3 w-3 text-pink-100" />}
        </div>
      </div>
    </div>
  )
}

function DaySeparator({ label }: { label: string }): JSX.Element {
  return (
    <div className="my-2 flex justify-center">
      <span className="rounded-full bg-white px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-500 shadow-sm">
        {label}
      </span>
    </div>
  )
}

function PendingBubble({
  msg,
  onDismiss,
}: {
  msg: PendingInstagramMessage
  onDismiss: (localId: string) => void
}): JSX.Element {
  const failed = msg.status === 'failed'
  return (
    <div className="flex justify-end">
      <div
        className={`relative max-w-[70%] rounded-2xl px-4 py-2 text-sm ${
          failed ? 'border border-red-200 bg-red-50 text-gray-700' : 'bg-[#E4405F] text-white opacity-70'
        }`}
      >
        <p className="whitespace-pre-wrap break-words">{msg.content_text}</p>
        <div className="mt-1 flex items-center justify-end gap-1">
          <span className={`text-[10px] ${failed ? 'text-red-500' : 'text-pink-100'}`}>
            {failed ? 'Not sent' : 'Sending…'}
          </span>
          {failed ? (
            <AlertCircle className="h-3 w-3 text-red-500" />
          ) : (
            <Loader2 className="h-3 w-3 animate-spin text-pink-100" />
          )}
          {failed && (
            <button
              onClick={() => onDismiss(msg.localId)}
              className="ml-0.5 text-gray-400 hover:text-gray-600"
              title="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export function InstagramChatThread({ chatId, isAssignedToMe, isAiAssigned, aiAgentName, onOpenTransfer, onOpenDetails }: Props): JSX.Element {
  const {
    chat,
    messages,
    handoffEvents,
    replyWindowGuard,
    conversations,
    pending,
    loading,
    loadingMore,
    hasMore,
    sendMessage,
    createTicket,
    dismissPending,
    loadMore,
  } = useInstagramChatMessages(chatId)
  const [input, setInput] = useState('')
  const user = useAuthStore((s) => s.user)
  const effectiveIsAssignedToMe =
    isAssignedToMe !== undefined
      ? isAssignedToMe
      : user?.agentId && chat?.assigned_agent_id
        ? chat.assigned_agent_id === user.agentId
        : true
  const effectiveIsAiAssigned = isAiAssigned !== undefined ? isAiAssigned : Boolean(chat?.ai_assigned)
  const effectiveAiAgentName = aiAgentName ?? chat?.ai_agent_name ?? null
  const { actors: typingActors } = useChannelTyping('instagram', chatId)
  const { signalTyping, stopTyping } = useTypingSignal('instagram', chatId)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creatingTicket, setCreatingTicket] = useState(false)
  const [ticketResult, setTicketResult] = useState<{ referenceId: string } | null>(null)
  const fetchingMoreRef = useRef(false)
  const { containerRef, atBottom, unseen, scrollToBottom, handleScroll: stickyHandleScroll } = useStickyScroll(
    `${chatId}:${messages.length}:${pending.length}`,
  )

  const onScroll = useCallback(() => {
    stickyHandleScroll()
    if (fetchingMoreRef.current || !hasMore || loadingMore) return
    const el = containerRef.current
    if (!el) return
    if (el.scrollTop <= el.clientHeight * 0.2) {
      const oldScrollHeight = el.scrollHeight
      const oldScrollTop = el.scrollTop
      fetchingMoreRef.current = true
      void loadMore().finally(() => {
        requestAnimationFrame(() => {
          const next = containerRef.current
          if (!next) return
          const added = next.scrollHeight - oldScrollHeight
          if (added > 0) {
            next.scrollTop = oldScrollTop + added
          }
          fetchingMoreRef.current = false
        })
      })
    }
  }, [hasMore, loadingMore, loadMore, stickyHandleScroll, containerRef])

  // Only enforced when the tenant has channel_chat_guards ON (server-reported). Fails open:
  // with no inbound message loaded we can't tell, so the server remains the final check.
  const windowExpired = useMemo(() => {
    if (!replyWindowGuard) return false
    const lastInbound = messages
      .filter((m) => m.direction === 'inbound')
      .reduce((max, m) => Math.max(max, new Date(m.sent_at).getTime()), 0)
    if (lastInbound === 0) return false
    return Date.now() - lastInbound > 24 * 60 * 60 * 1000
  }, [messages, replyWindowGuard])
  const groups = useMemo(() => groupMessagesByTicket(messages), [messages])
  const handoffPlacement = useMemo(
    () => placeHandoffEvents(messages, handoffEvents),
    [messages, handoffEvents],
  )
  const conversationById = useMemo(
    () => new Map(conversations.map((c) => [c.id, c])),
    [conversations],
  )
  const unlinkedCount = useMemo(
    () => messages.filter((m) => !m.conversation_id).length,
    [messages],
  )
  const dayStarts = useMemo(
    () => dayBoundaries(groups.flatMap((g) => g.messages)),
    [groups],
  )

  useEffect(() => {
    setTicketResult(null)
    setError(null)
    setInput('')
  }, [chatId])

  async function handleCreateTicket(): Promise<void> {
    if (!effectiveIsAssignedToMe || creatingTicket || unlinkedCount === 0) return
    setCreatingTicket(true)
    setError(null)
    try {
      const result = await createTicket()
      if (result) setTicketResult({ referenceId: result.referenceId })
    } catch {
      setError('Could not create the conversation.')
    } finally {
      setCreatingTicket(false)
    }
  }

  async function handleSend(): Promise<void> {
    if (!effectiveIsAssignedToMe || !chat || chat.status !== 'active' || windowExpired) return
    if (!input.trim() || sending) return
    stopTyping()
    setSending(true)
    setError(null)
    try {
      await sendMessage(input)
      setInput('')
    } catch (err) {
      const errBody = (err as { body?: { error?: string; message?: string } })?.body
      const code = errBody?.error
      const msg = errBody?.message
      setError(
        code === 'outside_messaging_window'
          ? 'Customer must message first (24h window expired).'
          : code === 'no_credentials'
            ? (msg || 'Instagram account is not connected or credentials are missing.')
            : 'Message could not be sent.',
      )
    } finally {
      setSending(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  if (!chatId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-gray-50">
        <MessageCircle className="mb-3 h-12 w-12 text-gray-300" />
        <p className="text-sm text-gray-400">Select an Instagram chat to start messaging</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E4405F]/10">
          <InstagramIcon className="h-5 w-5 text-[#E4405F]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-800">
            <button type="button" onClick={onOpenDetails} className="hover:text-blue-600">
              {chat?.sender_name || chat?.sender_id || 'Instagram Chat'}
            </button>
          </h3>
          <p className="truncate text-xs text-gray-400">
            Instagram Direct
          </p>
        </div>
        <button
          onClick={() => void handleCreateTicket()}
          disabled={!effectiveIsAssignedToMe || !chat || chat.status !== 'active' || unlinkedCount === 0 || creatingTicket}
          title={
            unlinkedCount === 0
              ? 'Every message is already on a conversation'
              : `Create a conversation from ${unlinkedCount} unlinked message${unlinkedCount === 1 ? '' : 's'}`
          }
          className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
        >
          <Ticket className="h-3.5 w-3.5" />
          {creatingTicket ? 'Creating…' : 'Create Conversation'}
          {unlinkedCount > 0 && !creatingTicket && (
            <span className="rounded-full bg-[#E4405F]/15 px-1.5 text-[10px] font-semibold text-[#E4405F]">
              {unlinkedCount}
            </span>
          )}
        </button>
        {(effectiveIsAssignedToMe || effectiveIsAiAssigned) && onOpenTransfer && (
          <ToolButton label="Transfer" icon={<ArrowRightLeft className="h-4 w-4" />} onClick={onOpenTransfer} />
        )}
      </div>

      {effectiveIsAiAssigned && (
        <div className="flex items-center justify-between border-b border-purple-200 bg-purple-50 px-4 py-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-purple-700">
            <Bot className="h-3.5 w-3.5" />
            {effectiveAiAgentName ?? 'AI Agent'} is handling this chat
          </span>
          {!effectiveIsAssignedToMe && (
            <button
              type="button"
              onClick={() => void api.post(`/agent/instagram-chats/${chatId}/assign`, {}).then(() => {}).catch(() => {})}
              className="flex items-center gap-1.5 rounded-lg border border-purple-300 bg-white px-3 py-1 text-xs font-medium text-purple-700 transition-all hover:bg-purple-50 active:scale-[0.97]"
            >
              <UserPlus className="h-3 w-3" />
              Assign to Me
            </button>
          )}
        </div>
      )}

      {ticketResult && (
        <div className="flex items-center justify-between border-b border-green-200 bg-green-50 px-4 py-2">
          <span className="text-xs text-green-700">
            Conversation created: <strong>{ticketResult.referenceId}</strong>
          </span>
          <button onClick={() => setTicketResult(null)} className="text-gray-400 hover:text-gray-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Messages, grouped by the conversation they belong to */}
      <div className="relative flex-1 overflow-hidden">
      <div ref={containerRef} onScroll={onScroll} className="h-full overflow-y-auto thin-scrollbar px-4 py-4">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
            Loading older messages…
          </div>
        )}
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-400">Loading messages…</div>
        ) : messages.length === 0 && pending.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-400">No messages yet</div>
        ) : (
          <div className="flex flex-col gap-2">
            {groups.map((group) =>
              group.type === 'ticket' ? (
                <TicketGroupBlock
                  key={group.key}
                  conversationId={group.conversationId}
                  conversation={conversationById.get(group.conversationId)}
                  messageCount={group.messages.length}
                  accentClassName="text-[#E4405F]"
                >
                  {group.messages.map((msg) => (
                    <Fragment key={msg.id}>
                      {dayStarts.has(msg.id) && <DaySeparator label={dayStarts.get(msg.id)!} />}
                      {handoffPlacement.before.get(msg.id)?.map((e) => <HandoffDivider key={e.id} event={e} />)}
                      <MessageBubble msg={msg} />
                    </Fragment>
                  ))}
                </TicketGroupBlock>
              ) : (
                group.messages.map((msg) => (
                  <Fragment key={msg.id}>
                    {dayStarts.has(msg.id) && <DaySeparator label={dayStarts.get(msg.id)!} />}
                    {handoffPlacement.before.get(msg.id)?.map((e) => <HandoffDivider key={e.id} event={e} />)}
                    <MessageBubble msg={msg} />
                  </Fragment>
                ))
              ),
            )}
            {handoffPlacement.trailing.map((e) => <HandoffDivider key={e.id} event={e} />)}
            {pending.map((p) => (
              <PendingBubble key={p.localId} msg={p} onDismiss={dismissPending} />
            ))}
          </div>
        )}
      </div>

      {/* Jump back to the newest message; badge counts what arrived meanwhile. */}
      {!atBottom && (
        <button
          onClick={() => scrollToBottom()}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-lg ring-1 ring-gray-200 transition-colors hover:bg-gray-50"
          title="Jump to latest"
        >
          <ChevronDown className="h-4 w-4" />
          {unseen > 0 && (
            <span className="rounded-full bg-[#E4405F] px-1.5 py-0.5 text-[10px] font-bold text-white">
              {unseen}
            </span>
          )}
        </button>
      )}
      </div>

      <TypingIndicator actors={typingActors} />

      {error && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-1.5 text-[11px] text-red-600">{error}</div>
      )}

      {effectiveIsAssignedToMe && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); signalTyping() }}
            onKeyDown={handleKeyDown}
            placeholder={windowExpired ? 'Customer must message first (24h window expired)' : 'Type a reply…'}
            disabled={!effectiveIsAssignedToMe || !chat || chat.status !== 'active' || windowExpired || sending}
            className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none focus:border-[#E4405F] focus:ring-1 focus:ring-[#E4405F]/20 disabled:opacity-50"
          />
          <button
            onClick={() => void handleSend()}
            disabled={!effectiveIsAssignedToMe || !chat || chat.status !== 'active' || windowExpired || !input.trim() || sending}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E4405F] text-white transition-colors hover:bg-[#d63384] disabled:opacity-40 disabled:hover:bg-[#E4405F]"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      )}
    </div>
  )
}
